import { useEffect, useState } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { ArrowUp } from "lucide-react";

import Home from "./pages/Home/index";
import SearchResults from "./pages/SearchResults";
import Navbar from "./components/Navbar/Navbar";
import ConfirmationPage from "./pages/ConfirmationPage";
import CheckoutPage from "./pages/CheckoutPage";
import SurveyPage from "./pages/SurveyPage";
import ManageBookingPage from "./pages/ManageBookingPage";
import SupportPage from "./pages/SupportPage";
import Footer from "./components/Footer/Footer";
import { FormProvider } from "./context/FormContext";
import { CurrencyProvider } from "./context/CurrencyContext";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import CookiePolicy from "./cookieConsent/CookiePolicy";
import { HelmetProvider } from "react-helmet-async";
import LandingPageContainer from "./landingPages/LandingPageContainer";
import LandingSearch from "./landingPages/LandingSearch";
import RentalVoucherPage from "./pages/RentalVoucherPage";
import FeedbackPage from "./feedback/FeedbackPage";
import { ConsentProvider } from './cookieConsent';
import ConsentBanner from './cookieConsent/ConsentBanner';
import TermsAndConditionsPage from "../src/pages/TermsAndConditionsPage";
import FAQPage from "./pages/FaqPage";
import AboutUs from "./pages/AboutUs";
import { API_BASE_URL} from "./api";
import SubscribeConfirmPage from "./pages/Home/sections/SubscribeConfirmPage";
import UnsubscribePage from "./pages/Home/sections/UnsubscribePage";

// ScrollToTop component to handle scroll restoration on route changes
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

export default function App() {
  const [showScroll, setShowScroll] = useState(false);
  const location = useLocation();
  const [canUseTestMode, setCanUseTestMode] = useState(false);

  // // SESSION TIMER LOGIC: Exclude Home and Confirmation Page
  // useEffect(() => {
  //   const currentPage = location.pathname;

  //   // Skip timer for Home & Confirmation pages
  //   if (currentPage === "/confirmation" || currentPage === "/manage-booking" || currentPage === "/support" || currentPage === "/survey") {
  //     console.log(`Session timer skipped for: ${currentPage}`);
  //     return;
  //   }

  //    if (location.pathname === "/") {
  //     const incompleteBooking = localStorage.getItem("incompleteBooking");
  //     // const shownOnce = sessionStorage.getItem("incompleteAlertShown");

  //     if (!incompleteBooking){
  //       return;
  //     }
  //     else{
  //       const timeoutDuration = 3600000; // 30 minutes
  //       const timer = setTimeout(() => {
  //         alert("⚠️ Your session has expired. Redirecting to home page.");
  //         localStorage.removeItem("incompleteBooking");
  //         window.location.replace("/");
  //       }, timeoutDuration);

  //       return () => clearTimeout(timer);
  //     }
  //   }

  //   // Start timer for other pages only
  //   const timeoutDuration = 3600000; // 30 minutes
  //   const timer = setTimeout(() => {
  //     alert("⚠️ Your session has expired. Redirecting to home page.");
  //     localStorage.removeItem("incompleteBooking");
  //     window.location.replace("/");
  //   }, timeoutDuration);

  //   return () => clearTimeout(timer);
  // }, [location.pathname]);

  useEffect(() => {
    fetch(`${API_BASE_URL}/init`, {
      method: "GET",
      credentials: "include",
    });
  }, []);

  useEffect(() => {
    fetch(`${API_BASE_URL}/features`, {
      method: "GET",
      credentials: "include",
    })
      .then((res) => res.json())
      .then((data) => setCanUseTestMode(data.canUseTestMode ?? false))
      .catch(() => setCanUseTestMode(false));
  }, []);

  useEffect(() => {
    const checkScrollTop = () => {
      setShowScroll(window.scrollY > 200);
    };
    window.addEventListener("scroll", checkScrollTop);
    return () => window.removeEventListener("scroll", checkScrollTop);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <HelmetProvider>
      <ConsentProvider>
        <CurrencyProvider>
          <FormProvider canUseTestMode={canUseTestMode}>
            <ScrollToTop />
            <div className="flex flex-col min-h-screen">
              <Navbar />

              <main className="flex-grow">
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/results" element={<SearchResults />} />
                  <Route path="/confirmation" element={<ConfirmationPage />} />
                  <Route path="/checkout" element={<CheckoutPage />} />
                  <Route path="/survey" element={<SurveyPage />} />
                  <Route
                    path="/manage-booking"
                    element={<ManageBookingPage />}
                  />
                  <Route path="/confirm" element={<SubscribeConfirmPage />} />
                  <Route path="/unsubscribe" element={<UnsubscribePage />} />
                  <Route path="/support" element={<SupportPage />} />
                  <Route path="/policy" element={<PrivacyPolicy />} />
                  <Route path="/cookie-policy" element={<CookiePolicy />} />
                  <Route
                    path="/terms-and-conditions"
                    element={<TermsAndConditionsPage />}
                  />
                  <Route
                    path="/rental-voucher"
                    element={<RentalVoucherPage />}
                  />
                  <Route path="/about-us" element={<AboutUs />} />
                  {/* NEW: SEO Landing Page Routes based on Verified Patterns [cite: 5, 58, 107, 152, 206] */}
                  <Route
                    path="/vendors/:vendorSlug"
                    element={<LandingPageContainer />}
                  />
                  <Route
                    path="/countries/:countrySlug"
                    element={<LandingPageContainer />}
                  />
                  <Route
                    path="/rent-a-car/*"
                    element={<LandingPageContainer />}
                  />
                  <Route
                    path="/locations/:locationSlug"
                    element={<LandingPageContainer />}
                  />
                  <Route
                    path="/vendors/:vendorSlug/locations/:locationSlug"
                    element={<LandingPageContainer />}
                  />
                  <Route path="/car-rental/*" element={<LandingSearch />} />
                  <Route path="/feedback" element={<FeedbackPage />} />
                  <Route path="/faqs" element={<FAQPage />} />
                </Routes>
              </main>

              {showScroll && (
                <button
                  onClick={scrollToTop}
                  className="fixed bottom-6 right-6 p-3 rounded-full bg-primary text-white shadow-lg hover:bg-primary-hover transition"
                >
                  <ArrowUp size={20} />
                </button>
              )}

           <Footer/>
              <ConsentBanner />
            </div>
          </FormProvider>
        </CurrencyProvider>
      </ConsentProvider>
    </HelmetProvider>
  );
}
